import React, { useState, useRef, useEffect } from 'react';
import { documentApi } from '../../../api/documentApi';
import { getDocumentUrl } from '../../../api/documentApi';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  CircularProgress,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';

type DocumentType = 'MEDICAL' | 'NON_MEDICAL';

interface Document {
  id: string;
  name: string;
  url?: string;
  path?: string;
  uploadedAt: string;
  size: number;
  type: DocumentType;
  description?: string;
}

interface DocumentManagementDialogProps {
  open: boolean;
  onClose: () => void;
  appointmentId: string;
  initialDocuments?: Document[];
}

const documentTypes = [
  { value: 'MEDICAL', label: 'Medical' },
  { value: 'NON_MEDICAL', label: 'Non-Medical' },
];

const DocumentManagementDialog: React.FC<DocumentManagementDialogProps> = ({
  open,
  onClose,
  appointmentId,
  initialDocuments = [],
}) => {
  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState<number>(-1);

  const [documents, setDocuments] = useState<Document[]>(initialDocuments);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [documentType, setDocumentType] = useState<DocumentType>('MEDICAL');
  const [documentDescription, setDocumentDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);

  // Fetch documents when dialog opens
  useEffect(() => {
    if (open && appointmentId) {
      documentApi.getDocuments(appointmentId)
        .then((docs) => {
          console.log('Fetched documents:', docs);
          const mapped = docs.map((d: any) => ({ ...d, url: d.url || d.path }));
          setDocuments(mapped);
        })
        .catch(() => setDocuments([]));
    }
  }, [open, appointmentId]);

  const refreshDocuments = () => {
    if (appointmentId) {
      documentApi.getDocuments(appointmentId)
        .then((docs) => {
          console.log('Refreshed documents:', docs);
          const mapped = docs.map((d: any) => ({ ...d, url: d.url || d.path }));
          setDocuments(mapped);
        })
        .catch((error) => {
          console.error('Error refreshing documents:', error);
          setDocuments([]);
        });
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    setSelectedFile(files[0]);
  };

  const handleUploadClick = async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    setUploadError(null);
    try {
      const uploadResult = await documentApi.uploadDocument(
        appointmentId,
        selectedFile,
        documentType,
        documentDescription
      );
      console.log('Upload result:', uploadResult);
      
      // Wait a moment for backend to process, then refresh
      setTimeout(() => {
        refreshDocuments();
      }, 500);
      
      setDocumentType('MEDICAL');
      setDocumentDescription('');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e: any) {
      setUploadError(e?.message || 'Failed to upload document');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await documentApi.deleteDocument(documentId);
      refreshDocuments();
    } catch (e) {
      alert('Failed to delete document');
    }
  };


  const handleEditDocument = (documentId: string) => {
    const docToEdit = documents.find(doc => doc.id === documentId);
    if (docToEdit) {
      setDocumentType(docToEdit.type);
      setDocumentDescription(docToEdit.description || '');
      setEditingDocId(documentId);
    }
  };

  const handleUpdateDocument = () => {
    if (!editingDocId) return;
    
    setDocuments(prev => 
      prev.map(doc => 
        doc.id === editingDocId 
          ? { ...doc, type: documentType, description: documentDescription }
          : doc
      )
    );
    setEditingDocId(null);
    setDocumentType('MEDICAL');
    setDocumentDescription('');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Manage Documents</DialogTitle>
      <DialogContent>
        <Box mb={3} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
          />
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CloudUploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {selectedFile ? selectedFile.name : 'Select File'}
            </Button>
            <TextField
              select
              label="Type"
              value={documentType}
              onChange={e => setDocumentType(e.target.value as DocumentType)}
              size="small"
              sx={{ minWidth: 140 }}
              disabled={isUploading}
            >
              {documentTypes.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description"
              value={documentDescription}
              onChange={e => setDocumentDescription(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
              disabled={isUploading}
            />
            {editingDocId ? (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleUpdateDocument}
                disabled={isUploading || !documentDescription}
                sx={{ ml: 2 }}
              >
                Update
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleUploadClick}
                disabled={isUploading || !selectedFile || !documentDescription}
                sx={{ ml: 2 }}
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            )}
          </Box>
          {uploadError && (
            <Typography variant="caption" color="error" mt={1} display="block">
              {uploadError}
            </Typography>
          )}
          <Typography variant="caption" color="textSecondary" mt={1} display="block">
            Supported formats: PDF, DOC, XLS, JPG, PNG (Max 10MB)
          </Typography>
          
          
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" gutterBottom>
          Attached Documents ({documents.length})
        </Typography>

        {documents.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="textSecondary">
              No documents attached to this appointment.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Document Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Uploaded On</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <FileIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          {doc.name}
                        </Typography>
                      </Box>
                      {doc.description && (
                        <Typography variant="caption" color="textSecondary">
                          {doc.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={doc.type === 'MEDICAL' ? 'Medical' : 'Non-Medical'}
                        size="small"
                        color={doc.type === 'MEDICAL' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(doc.uploadedAt).toLocaleDateString()}
                      <Typography variant="caption" display="block" color="textSecondary">
                        {new Date(doc.uploadedAt).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatFileSize(doc.size)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setPreviewIndex(documents.findIndex(d => d.id === doc.id));
                            setPreviewOpen(true);
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEditDocument(doc.id)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteDocument(doc.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>

      {/* Document Preview Modal */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Document Preview
        </DialogTitle>
        <DialogContent sx={{ minHeight: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          {previewIndex >= 0 && previewIndex < documents.length && documents[previewIndex] && documents[previewIndex].url ? (
            (() => {
              const doc = documents[previewIndex];
              const url = getDocumentUrl(doc.url || doc.path || '');
              const ext = doc.name?.split('.').pop()?.toLowerCase() || '';
              if (["jpg","jpeg","png","gif","bmp","webp"].includes(ext)) {
                return <img src={url} alt={doc.name} style={{ maxWidth: '100%', maxHeight: 500 }} />;
              } else if (ext === "pdf") {
                return <embed src={url} type="application/pdf" width="100%" height="500px" />;
              } else {
                return <Typography>Cannot preview this file type. {doc.url && <a href={url} target="_blank" rel="noopener noreferrer">Download</a>}</Typography>;
              }
            })()
          ) : (
            <Typography color="error">No document selected or document is invalid.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={previewIndex <= 0} onClick={() => setPreviewIndex(i => i - 1)}>
            Prev
          </Button>
          <Button disabled={previewIndex >= documents.length - 1} onClick={() => setPreviewIndex(i => i + 1)}>
            Next
          </Button>
          <Button onClick={() => setPreviewOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default DocumentManagementDialog;
